import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import TreeSidebar from './TreeSidebar';
import ContentHeader from './ContentHeader';
import AppBody from './AppBody';
import RichTextEditor from './RichTextEditor';
import { useCategoryMutations } from '../hooks/data/useCategoryMutations';
import { useBookmarkMutations } from '../hooks/data/useBookmarkMutations';
import { useNoteMutations } from '../hooks/data/useNoteMutations';
import { useBackgroundSync } from '../hooks/data/useBackgroundSync';
import { useCacheStore } from '../hooks/data/useCacheStore';
import { useCategoryContext } from '../contexts/CategoryContext';
import { useWebAuthn } from '../contexts/WebAuthnContext';
import { refetchAndCache } from '../hooks/data/refetchAndCache';
import { ALL_REFETCH_KEYS } from '../utils/refetchKeys';
import { useViewMode } from '../hooks/useViewMode';

const SIDEBAR_MIN_WIDTH = 200;
const SIDEBAR_MAX_WIDTH = 500;
const SIDEBAR_DEFAULT_WIDTH = 280;

/**
 * FileExplorerLayout: Three-panel file-explorer layout.
 *
 * - Desktop (>= md): TreeSidebar fixed on left (280px), ContentHeader + MainContentArea on right
 * - Mobile (< md): Sidebar hidden, hamburger button opens a MUI Drawer with TreeSidebar
 *
 * Reads navigation state from CategoryContext (no props needed).
 *
 * Requirements: 6.3, 6.7, 6.9, 8.1, 8.2, 8.3, 12.1, 12.2, 12.4
 */
const FileExplorerLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_DEFAULT_WIDTH);
  const isResizing = useRef(false);

  // Add Category dialog state
  const [showAddCategoryDialog, setShowAddCategoryDialog] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [addCategoryError, setAddCategoryError] = useState('');
  const [addCategoryLoading, setAddCategoryLoading] = useState(false);
  // Parent the new category will be created under. undefined = "use current folder"
  // (toolbar action); null = force root (nav-panel Bookmarks root action).
  const [addCategoryParentId, setAddCategoryParentId] = useState<
    string | null | undefined
  >(undefined);
  const { addCategory } = useCategoryMutations();
  const { addNote } = useNoteMutations();
  const { selectedCategory, selectedNoteCategory, selectNote } =
    useCategoryContext();

  // Add Bookmark dialog state
  const [showAddBookmarkDialog, setShowAddBookmarkDialog] = useState(false);
  const [newBookmarkName, setNewBookmarkName] = useState('');
  const [newBookmarkUrl, setNewBookmarkUrl] = useState('');
  const [newBookmarkNotes, setNewBookmarkNotes] = useState('');
  const [addBookmarkNameError, setAddBookmarkNameError] = useState('');
  const [addBookmarkLoading, setAddBookmarkLoading] = useState(false);
  const { addBookmark } = useBookmarkMutations();
  const { setSnackbar } = useWebAuthn();

  // Background sync — subscribes to Realtime for live updates
  useBackgroundSync();
  const cache = useCacheStore();
  const { viewMode, setViewMode } = useViewMode();

  const handleRefresh = useCallback(async () => {
    // Refetch the full data set (bookmarks + categories AND the three notes
    // keys) so Refresh pulls notes too — previously it only refetched bookmarks.
    await Promise.all(
      ALL_REFETCH_KEYS.map((key) => refetchAndCache(key, cache)),
    );
  }, [cache]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleAddBookmark = () => {
    setNewBookmarkName('');
    setNewBookmarkUrl('');
    setNewBookmarkNotes('');
    setAddBookmarkNameError('');
    setShowAddBookmarkDialog(true);
  };

  const handleAddBookmarkCancel = () => {
    setShowAddBookmarkDialog(false);
    setNewBookmarkName('');
    setNewBookmarkUrl('');
    setNewBookmarkNotes('');
    setAddBookmarkNameError('');
  };

  const handleAddBookmarkSave = async () => {
    const trimmed = newBookmarkName.trim();
    if (!trimmed) {
      setAddBookmarkNameError('Bookmark name is required.');
      return;
    }
    if (trimmed.length > 255) {
      setAddBookmarkNameError('Bookmark name must not exceed 255 characters.');
      return;
    }

    setAddBookmarkLoading(true);
    setAddBookmarkNameError('');

    const result = await addBookmark({
      bookmark_name: trimmed,
      bookmark_url: newBookmarkUrl,
      bookmark_notes: newBookmarkNotes,
      category_id: selectedCategory?.category_id ?? null,
    });

    setAddBookmarkLoading(false);

    if (result.success) {
      setShowAddBookmarkDialog(false);
      setNewBookmarkName('');
      setNewBookmarkUrl('');
      setNewBookmarkNotes('');
      setAddBookmarkNameError('');
    } else {
      setSnackbar({
        open: true,
        message: result.error || 'Could not save bookmark. Please try again.',
        severity: 'error',
      });
    }
  };

  // parentId: undefined = create under currently focused folder (toolbar);
  // null = force creation at root (nav-panel Bookmarks root node).
  const handleAddCategory = (parentId?: string | null) => {
    setAddCategoryParentId(parentId);
    setNewCategoryName('');
    setAddCategoryError('');
    setShowAddCategoryDialog(true);
  };

  const handleAddCategoryAtRoot = () => handleAddCategory(null);

  // Create a new note in the currently selected note category and open it.
  const handleAddNote = useCallback(async () => {
    const result = await addNote({
      note_title: 'Untitled note',
      note_content: '',
      category_id: selectedNoteCategory?.category_id ?? null,
    });
    if (result.success && result.note) {
      selectNote(result.note);
    } else {
      setSnackbar({
        open: true,
        message: result.error || 'Could not create note. Please try again.',
        severity: 'error',
      });
    }
  }, [addNote, selectedNoteCategory, selectNote, setSnackbar]);

  // Sidebar resize handlers
  const handleResizeStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      isResizing.current = true;

      const handleMove = (moveEvent: MouseEvent | TouchEvent) => {
        if (!isResizing.current) return;
        const clientX =
          'touches' in moveEvent
            ? moveEvent.touches[0].clientX
            : moveEvent.clientX;
        const newWidth = Math.min(
          SIDEBAR_MAX_WIDTH,
          Math.max(SIDEBAR_MIN_WIDTH, clientX),
        );
        setSidebarWidth(newWidth);
      };

      const handleEnd = () => {
        isResizing.current = false;
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);
      };

      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleMove);
      document.addEventListener('touchend', handleEnd);
    },
    [],
  );

  const handleAddCategoryCancel = () => {
    setShowAddCategoryDialog(false);
    setNewCategoryName('');
    setAddCategoryError('');
  };

  const handleAddCategorySave = async () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed) {
      setAddCategoryError('Category name is required.');
      return;
    }
    if (trimmed.length > 100) {
      setAddCategoryError('Category name must be 100 characters or fewer.');
      return;
    }

    setAddCategoryLoading(true);
    setAddCategoryError('');

    // When the caller forced a parent (null = root), honor it; otherwise
    // default to the currently focused folder.
    const parentId =
      addCategoryParentId !== undefined
        ? addCategoryParentId
        : selectedCategory?.category_id ?? null;
    const result = await addCategory(trimmed, parentId);

    setAddCategoryLoading(false);

    if (result.success) {
      setShowAddCategoryDialog(false);
      setNewCategoryName('');
      setAddCategoryError('');
    } else if (result.constraintViolation) {
      setAddCategoryError(
        result.error ||
          'A category with that name already exists at this level.',
      );
    } else {
      setAddCategoryError(result.error || 'Failed to create category.');
    }
  };

  return (
    <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* Desktop sidebar — always visible above md breakpoint */}
      {!isMobile && (
        <>
          <Box
            sx={{
              width: sidebarWidth,
              minWidth: SIDEBAR_MIN_WIDTH,
              maxWidth: SIDEBAR_MAX_WIDTH,
              flexShrink: 0,
              borderRight: 1,
              borderColor: 'divider',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            <TreeSidebar
              open={true}
              onClose={() => {}}
              onAddCategory={handleAddCategoryAtRoot}
            />
          </Box>
          {/* Resize handle */}
          <Box
            onMouseDown={handleResizeStart}
            onTouchStart={handleResizeStart}
            sx={{
              width: 4,
              cursor: 'col-resize',
              flexShrink: 0,
              '&:hover': {
                bgcolor: 'primary.main',
                opacity: 0.3,
              },
              transition: 'background-color 0.2s',
            }}
          />
        </>
      )}

      {/* Mobile drawer — shown below md breakpoint */}
      {isMobile && (
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={handleDrawerClose}
          PaperProps={{
            sx: {
              width: '100vw',
            },
          }}
        >
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              display: 'flex',
              justifyContent: 'flex-end',
              p: 1,
              bgcolor: 'background.paper',
            }}
          >
            <IconButton
              onClick={handleDrawerClose}
              aria-label="Close navigation"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <TreeSidebar
            open={drawerOpen}
            onClose={handleDrawerClose}
            onAddCategory={handleAddCategoryAtRoot}
          />
        </Drawer>
      )}

      {/* Right panel: ContentHeader + MainContentArea */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        {/* Mobile hamburger + ContentHeader row (sticky, never scrolls) */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {isMobile && (
            <IconButton
              aria-label="Open category navigation"
              onClick={handleDrawerOpen}
              sx={{ ml: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box sx={{ flex: 1 }}>
            <ContentHeader
              onAddBookmark={handleAddBookmark}
              onAddCategory={handleAddCategory}
              onRefresh={handleRefresh}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />
          </Box>
        </Box>

        {/* Main content (scrolls independently) */}
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          <AppBody viewMode={viewMode} onAddNote={handleAddNote} />
        </Box>
      </Box>

      {/* Add Category Dialog */}
      <Dialog
        open={showAddCategoryDialog}
        onClose={handleAddCategoryCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {addCategoryParentId === undefined && selectedCategory
            ? `Add Subcategory to "${selectedCategory.category_name}"`
            : 'Add Category'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            variant="outlined"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            inputProps={{ maxLength: 100 }}
            error={!!addCategoryError}
            helperText={addCategoryError}
            disabled={addCategoryLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleAddCategoryCancel}
            disabled={addCategoryLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddCategorySave}
            variant="contained"
            disabled={addCategoryLoading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Bookmark Dialog */}
      <Dialog
        open={showAddBookmarkDialog}
        onClose={handleAddBookmarkCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Bookmark</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            variant="outlined"
            value={newBookmarkName}
            onChange={(e) => {
              setNewBookmarkName(e.target.value);
              if (addBookmarkNameError) setAddBookmarkNameError('');
            }}
            inputProps={{ maxLength: 255 }}
            error={!!addBookmarkNameError}
            helperText={addBookmarkNameError}
            disabled={addBookmarkLoading}
          />
          <TextField
            margin="dense"
            label="URL"
            fullWidth
            variant="outlined"
            value={newBookmarkUrl}
            onChange={(e) => setNewBookmarkUrl(e.target.value)}
            disabled={addBookmarkLoading}
          />
          <Box sx={{ mt: 1.5 }}>
            <RichTextEditor
              initialContent={newBookmarkNotes}
              onChange={setNewBookmarkNotes}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleAddBookmarkCancel}
            disabled={addBookmarkLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddBookmarkSave}
            variant="contained"
            disabled={addBookmarkLoading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FileExplorerLayout;
